import React, { useEffect } from 'react';
import { Zap, Heart, Sparkles, TrendingUp, Clock } from 'lucide-react';
import styled, { keyframes } from 'styled-components';

// Keyframe animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(3deg);
  }
`;

const floatReverse = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(15px) rotate(-3deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const slideIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const cardFloat = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(236, 100, 53, 0.4), 0 0 40px rgba(236, 100, 53, 0.2);
  }
  50% {
    box-shadow: 0 0 30px rgba(236, 100, 53, 0.6), 0 0 60px rgba(236, 100, 53, 0.4);
  }
`;

// Styled components
const SceneContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(236, 100, 53, 0.05) 0%, rgba(255, 140, 66, 0.05) 50%, rgba(255, 159, 64, 0.05) 100%);
`;

const GameCard = styled.div<{ $delay: number; $position: { top: string; left: string; rotation: string; zIndex: number; translateX: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  width: 180px;
  height: 240px;
  background: linear-gradient(135deg, #EC6435 0%, #FF8C42 100%);
  border-radius: 20px;
  box-shadow: 
    0 12px 32px rgba(0, 0, 0, 0.3),
    0 0 30px rgba(236, 100, 53, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  animation: 
    ${slideIn} 0.8s ease-out ${props => props.$delay}s forwards,
    ${glowPulse} 3s ease-in-out infinite;
  transform-origin: center center;
  transform: translate(-50%, -50%) translateX(${props => props.$position.translateX}) rotate(${props => props.$position.rotation});
  z-index: ${props => props.$position.zIndex};
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    transform: translate(-50%, -50%) translateX(${props => props.$position.translateX}) rotate(${props => props.$position.rotation}) scale(1.08) translateY(-8px);
    z-index: 20;
    box-shadow: 
      0 16px 40px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(236, 100, 53, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
`;

const CardIcon = styled.div<{ $delay: number }>`
  margin-bottom: 10px;
  animation: ${pulse} ${props => 2 + props.$delay}s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 4px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  line-height: 1.2;
`;

const CardDescription = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  line-height: 1.3;
`;

const HotBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(6px);
  border-radius: 10px;
  padding: 4px 8px;
  font-size: 9px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;


const Particle = styled.div<{ $delay: number; $position: { top: string; left: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  width: 3px;
  height: 3px;
  background: radial-gradient(circle, rgba(236, 100, 53, 0.8) 0%, transparent 70%);
  border-radius: 50%;
  animation: 
    ${float} ${props => 6 + props.$delay}s ease-in-out infinite,
    ${pulse} ${props => 4 + props.$delay}s ease-in-out infinite;
  opacity: 0.5;
  z-index: 0;
`;

const GlowEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(236, 100, 53, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  animation: ${pulse} 4s ease-in-out infinite;
  z-index: 0;
`;

// Partially-hidden background cards for extra depth behind the main fan
const BackgroundCard = styled.div<{ $edge: 'top' | 'bottom'; $rotation: string }>`
  position: absolute;
  left: 50%;
  ${props => (props.$edge === 'top' ? 'top: -8%;' : 'bottom: -8%;')}
  width: 140px;
  height: 190px;
  background: linear-gradient(135deg, #EC6435 0%, #FF8C42 100%);
  border-radius: 16px;
  opacity: 0.6;
  transform: translateX(-50%) rotate(${props => props.$rotation});
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
`;

export const GamesAnimatedScene: React.FC = () => {
  const gameCards = [
    {
      icon: <Zap size={36} color="#ffffff" />,
      title: 'Quick Fire',
      description: 'Answer fast, match faster!',
      delay: 0,
      position: { top: '50%', left: '50%', rotation: '0deg', zIndex: 10, translateX: '0px' },
      hasBadge: true
    },
    {
      icon: <Heart size={32} color="#ffffff" fill="#ffffff" />,
      title: 'Would You Rather',
      description: 'Discover preferences',
      delay: 0.3,
      position: { top: '50%', left: '50%', rotation: '-20deg', zIndex: 9, translateX: '-110px' },
      hasBadge: false
    },
    {
      icon: <Sparkles size={32} color="#ffffff" />,
      title: 'Truth or Dare',
      description: 'Get playful together',
      delay: 0.6,
      position: { top: '50%', left: '50%', rotation: '20deg', zIndex: 8, translateX: '110px' },
      hasBadge: false
    },
  ];



  const particles = [
    { delay: 0, position: { top: '20%', left: '25%' } },
    { delay: 0.5, position: { top: '65%', left: '70%' } },
    { delay: 1, position: { top: '35%', left: '75%' } },
    { delay: 1.5, position: { top: '70%', left: '30%' } },
    { delay: 2, position: { top: '50%', left: '20%' } },
    { delay: 2.5, position: { top: '30%', left: '65%' } },
  ];

  return (
    <SceneContainer>
      <GlowEffect />

      {/* Partially-hidden background cards for extra depth */}
      <BackgroundCard $edge="top" $rotation="-6deg">
        <TrendingUp size={32} color="rgba(255,255,255,0.9)" />
      </BackgroundCard>
      <BackgroundCard $edge="bottom" $rotation="6deg">
        <Clock size={32} color="rgba(255,255,255,0.9)" />
      </BackgroundCard>

      {/* Floating particles */}
      {particles.map((particle, i) => (
        <Particle
          key={`particle-${i}`}
          $delay={particle.delay}
          $position={particle.position}
        />
      ))}

      {/* Game cards with overlapping fan design */}
      {gameCards.map((card, i) => (
        <GameCard
          key={`card-${i}`}
          $delay={card.delay}
          $position={card.position}
        >
          <CardIcon $delay={card.delay}>
            {card.icon}
          </CardIcon>
          <CardTitle>{card.title}</CardTitle>
          <CardDescription>{card.description}</CardDescription>
          {card.hasBadge && (
            <HotBadge>⚡ HOT</HotBadge>
          )}
        </GameCard>
      ))}
    </SceneContainer>
  );
};

